/**
 * Help Center home (/help): search first, then the most-read questions, then
 * every section. A search runs live under the box and is kept in the URL
 * (?q=) so a result page can be shared.
 */
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, MessageCircle } from 'lucide-react';
import { HELP_CENTER } from '@/content/help';
import { homeMeta } from '@/content/help/seo';
import { SI_LEADERS_DISCLOSURE } from '@/content/help/authors';
import { AI_LEADERSHIP } from '@/data/aiLeadership';
import HelpShell from '@/components/help/HelpShell';
import HelpSeo from '@/components/help/HelpSeo';
import HelpIcon from '@/components/help/HelpIcon';
import HelpSearch, { SearchResultList } from '@/components/help/HelpSearch';
import { useHelpSearch } from '@/components/help/useHelpSearch';

const PANEL = 'rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.015]';

export function ContactPanel() {
  return (
    <div className={`${PANEL} flex flex-col sm:flex-row sm:items-center gap-5 px-6 py-6 sm:px-8`}>
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#7A8ABF]/15 ring-1 ring-[#7A8ABF]/30">
        <MessageCircle className="h-5 w-5 text-[#C9D0EA]" strokeWidth={1.5} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[18px] font-medium text-white">Still need help?</p>
        <p className="mt-1 text-[14.5px] leading-relaxed text-white/65">Chat with the Defera Concierge, call or email us. We’re here for anything these articles don’t answer.</p>
      </div>
      <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-[14px] font-medium text-black hover:bg-white/90 whitespace-nowrap">
        Contact us <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function HelpHome() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const setQuery = (q) => setParams(q ? { q } : {}, { replace: true });
  const results = useHelpSearch(query, 30);
  const { sections, popular, articles } = HELP_CENTER;

  return (
    <HelpShell showSearch={false}>
      <HelpSeo meta={homeMeta(HELP_CENTER)} />

      <section className="px-4 sm:px-6 pt-16 sm:pt-24 pb-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[12px] uppercase tracking-[0.24em] text-[#AAB4D6]">Defera Help Center</p>
          <h1 className="mt-4 text-[40px] sm:text-[56px] leading-[1.05] font-semibold tracking-[-0.03em] text-white">
            How can we help?
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[16px] sm:text-[17px] leading-relaxed text-white/70">
            {articles.length > 1 ? `${articles.length} articles` : 'Articles'} on booking, paying and traveling with Defera, written by our SI leaders.
          </p>
          <div className="mt-9">
            <HelpSearch variant="hero" value={query} onChange={setQuery} autoFocus={false} />
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-6xl space-y-16">
          {query.trim() ? (
            <section aria-label="Search results" className="mx-auto max-w-3xl">
              <p className="mb-2 text-[13px] text-white/50">
                {results.length ? `${results.length} result${results.length === 1 ? '' : 's'} for “${query.trim()}”` : `No articles match “${query.trim()}”.`}
              </p>
              {results.length > 0
                ? <SearchResultList results={results} onPick={(a) => navigate(a.path)} />
                : <p className="py-6 text-[15px] text-white/70">Try fewer or different words, or browse the topics below.</p>}
            </section>
          ) : null}

          {!query.trim() && popular.length > 0 && (
            <section aria-labelledby="most-read">
              <h2 id="most-read" className="text-[22px] font-medium text-white">Most-asked questions</h2>
              <ul className="mt-5 grid gap-x-10 gap-y-1 sm:grid-cols-2">
                {popular.map((a) => (
                  <li key={a.id}>
                    <Link to={a.path} className="group flex items-start justify-between gap-4 border-b border-white/[0.07] py-3.5 text-[15.5px] text-white/85 hover:text-white">
                      <span>{a.title}</span>
                      <ArrowUpRight className="mt-1 h-4 w-4 flex-shrink-0 text-white/30 group-hover:text-white/70" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby="topics">
            <h2 id="topics" className="text-[22px] font-medium text-white">Browse by topic</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sections.map((s) => (
                <Link key={s.id} to={s.path} className={`${PANEL} group flex flex-col px-5 py-5 transition-colors hover:border-white/20`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/10 text-[#C9D0EA]">
                      <HelpIcon name={s.icon} className="h-5 w-5" />
                    </span>
                    <span className="text-[17px] font-medium text-white">{s.title}</span>
                  </div>
                  <p className="mt-3 flex-1 text-[14.5px] leading-relaxed text-white/65">{s.description}</p>
                  <div className="mt-4 flex items-center gap-2 text-[12.5px] text-white/45">
                    {s.authorProfile && (
                      <img src={s.authorProfile.portrait} alt="" className="h-5 w-5 rounded-full object-cover object-top ring-1 ring-white/15" loading="lazy" />
                    )}
                    <span>{s.authorProfile?.name}</span>
                    <span aria-hidden>·</span>
                    <span>{s.articles.length} article{s.articles.length === 1 ? '' : 's'}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section aria-labelledby="authors" className={`${PANEL} px-6 py-7 sm:px-8`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex -space-x-2.5">
                {AI_LEADERSHIP.map((p) => (
                  <img key={p.id} src={p.portrait} alt={p.name} title={`${p.name}, ${p.title}`} loading="lazy" className="h-11 w-11 rounded-full object-cover object-top ring-2 ring-black" />
                ))}
              </div>
              <div className="min-w-0">
                <h2 id="authors" className="text-[18px] font-medium text-white">Written by our SI leaders</h2>
                <p className="mt-1 text-[14.5px] leading-relaxed text-white/65">
                  {SI_LEADERS_DISCLOSURE} Each section is written by the leader who runs that part of Defera.{' '}
                  <Link to="/about" className="text-[#AAB4D6] underline decoration-white/20 underline-offset-2 hover:text-white">Meet them</Link>
                </p>
              </div>
            </div>
          </section>

          <ContactPanel />
        </div>
      </div>
    </HelpShell>
  );
}
